import banners from '@gin-betting/banners';
import core, { GQL } from '@gin-betting/core';
import { Map, OrderedSet } from 'immutable';
import { createSelector, Selector } from 'reselect';

import type { State } from 'types';
import type { BannerItemsByZoneId, FeaturedConfig, PromoState } from './types';
import { groupBannerItemsBySport } from './utils';

const {
    entities: {
        selectors: { selectSports },
    },
} = core;

const {
    selectors: { makeBannerItemsSelector },
} = banners;

export const selectPromo = (state: State): PromoState => state.promo;

export const selectStateFeaturedTournaments = createSelector(
    selectPromo,
    promoState => promoState.get('featuredTournaments')
);

export const selectFeaturedTournamentsConfigs = createSelector(
    selectStateFeaturedTournaments,
    (featuredTournaments): Map<string, FeaturedConfig> =>
        featuredTournaments.configs
);

export const selectFeaturedTournamentsCurrentConfigs = createSelector(
    selectStateFeaturedTournaments,
    (featuredTournaments): OrderedSet<string> =>
        featuredTournaments.currentConfigs
);

export const selectFeaturedTournamentsList = createSelector(
    selectFeaturedTournamentsConfigs,
    selectFeaturedTournamentsCurrentConfigs,
    (configs, currentConfigs): OrderedSet<FeaturedConfig> => {
        return currentConfigs
            .map(currentConfig => configs.get(currentConfig))
            .filter((config): config is FeaturedConfig => !!config)
            .toOrderedSet();
    }
);

export const selectFeaturedTournamentsFetchStatus = createSelector(
    selectStateFeaturedTournaments,
    featuredTournaments => featuredTournaments.fetchStatus
);

export const selectAreSportsAvailable = createSelector(selectSports, sports =>
    Boolean(sports.size)
);

export const makeSelectZones = (
    zoneIds: string[]
): Selector<any, BannerItemsByZoneId> => {
    const bannerItemSelectors = zoneIds.map(zoneId =>
        makeBannerItemsSelector(zoneId, GQL.BannerType.betting_sport_event)
    );

    // NOTE: "createSelector" type issue with rest args
    return (createSelector as any)(
        ...bannerItemSelectors,
        (
            ...bannerItemsArr: Array<
                ReturnType<typeof bannerItemSelectors[number]>
            >
        ): BannerItemsByZoneId => {
            // NOTE: zoneIds.length should be equal with bannerItemsArr.length
            return zoneIds.reduce<BannerItemsByZoneId>((acc, zoneId, index) => {
                const prev = acc[zoneId];

                const bannerItems = bannerItemsArr[index];

                if (!bannerItems) return acc;

                acc[zoneId] = groupBannerItemsBySport(bannerItems, prev);

                return acc;
            }, {});
        }
    );
};
